import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { t, translateError } from '../i18n.js';
import { api, authState } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import { parseServerDate } from '../utils/dates.js';
import AiAssist from '../components/shared/AiAssist.jsx';
import { openFile } from '../utils/openFile.js';
import { PdfFrame } from '../components/shared/PdfPages.jsx';
import DepartmentLabels from '../components/shared/DepartmentLabel.jsx';
import ComplexSharedDocuments from '../components/shared/ComplexSharedDocuments.jsx';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isPreviewable(fileType) {
  if (!fileType) return false;
  return fileType.startsWith('image/') || fileType === 'application/pdf';
}

function fileIcon(fileType) {
  if (!fileType) return '📄';
  if (fileType === 'external/link') return '🔗';
  if (fileType.startsWith('image/')) return '🖼️';
  if (fileType === 'application/pdf') return '📕';
  if (fileType.includes('word') || fileType.includes('document')) return '📝';
  if (fileType.includes('sheet') || fileType.includes('excel') || fileType.includes('csv')) return '📊';
  if (fileType.includes('presentation') || fileType.includes('powerpoint')) return '📊';
  if (fileType.includes('zip') || fileType.includes('compressed')) return '🗜️';
  if (fileType.includes('video')) return '🎬';
  if (fileType.includes('audio')) return '🎵';
  return '📄';
}

// Only http(s) links become clickable; anything else (javascript:, data:) is shown as text
function isHttpUrl(url) {
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch { return false; }
}

function externalServiceLabel(url) {
  if (!url) return 'Externe link';
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (host.includes('sharepoint.com') || host.includes('sharepoint')) return 'SharePoint';
    if (host.includes('onedrive.live.com') || host.includes('1drv.ms')) return 'OneDrive';
    if (host.includes('docs.google.com') || host.includes('drive.google.com')) return 'Google Drive';
    if (host.includes('dropbox.com')) return 'Dropbox';
    if (host.includes('notion.so')) return 'Notion';
    if (host.includes('confluence')) return 'Confluence';
    return host.replace('www.', '');
  } catch { return 'Externe link'; }
}

function timeAgo(dateStr) {
  if (!dateStr) return '-';
  const diff = Date.now() - parseServerDate(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Vandaag';
  if (days === 1) return 'Gisteren';
  if (days < 7) return `${days} dagen geleden`;
  if (days < 30) return `${Math.floor(days / 7)} weken geleden`;
  if (days < 365) return `${Math.floor(days / 30)} maanden geleden`;
  return `${Math.floor(days / 365)} jaar geleden`;
}

// ─── Folder tree helpers ──────────────────────────────────────────────────────

const isAutoFolder = (f) => !!(f?.commission_id || f?.category_id);
// The server marks the one folder per section that is the section's own (is_section_root).
// Another top-level folder visible to that section is an ordinary folder with its own name.
const isSectionRoot = (f) => !!f.is_section_root;

// Section root folders are labelled by their section, the same way the sidebar shows them.
function folderLabel(f) {
  return isSectionRoot(f) ? (f.section_name || f.name) : f.name;
}

// Folders whose parent is missing from the list (not visible to this user) count as top level.
function topLevelFolders(folders) {
  const ids = new Set(folders.map(f => f.id));
  return folders.filter(f => !f.parent_id || !ids.has(f.parent_id));
}

// Root → folder, stopping at a missing parent or a cycle.
function folderPath(folder, folders) {
  const path = [];
  const seen = new Set();
  let cur = folder;
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    path.unshift(cur);
    cur = folders.find(f => f.id === cur.parent_id);
  }
  return path;
}

// Depth-first list with full path labels, for folder <select>s.
function folderOptions(folders) {
  const out = [];
  const seen = new Set();
  const walk = (f) => {
    if (seen.has(f.id)) return;
    seen.add(f.id);
    out.push({ id: f.id, label: `${f.icon} ${folderPath(f, folders).map(folderLabel).join(' / ')}` });
    folders.filter(c => c.parent_id === f.id).forEach(walk);
  };
  topLevelFolders(folders).forEach(walk);
  return out;
}

// Lookup tables from the nested /api/settings/sections response.
function clubStructureNames(sectionTree) {
  const sections = {}, commissions = {}, categories = {};
  for (const s of sectionTree || []) {
    sections[s.id] = s.name;
    for (const c of s.commissions || []) {
      commissions[c.id] = c.name;
      for (const cat of c.categories || []) categories[cat.id] = cat.name;
    }
    for (const cat of s.categories || []) categories[cat.id] = cat.name;
  }
  return { sections, commissions, categories };
}

// Human-readable audience of a folder (audience comes from GET /api/documents/folders).
function audienceLabel(audience, names) {
  if (!audience) return '';
  switch (audience.type) {
    case 'all':        return 'Iedereen';
    case 'section':    return `Leden van sectie ${names.sections[audience.id] || '?'}`;
    case 'commission': return `Leden van ${names.commissions[audience.id] || 'commissie'}`;
    case 'categories': return audience.ids.map(id => names.categories[id]).filter(Boolean).join(', ') || 'Gekozen categorieën';
    default:           return 'Alleen beheerders';
  }
}

// ─── Small sub-components ─────────────────────────────────────────────────────

function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="display:inline;vertical-align:middle;margin-right:3px">
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

function DocumentsSkeleton() {
  return (
    <div style="padding:8px 0">
      {[1, 2, 3].map((i) => (
        <div key={i} style="display:flex;align-items:center;gap:12px;padding:14px 16px;border-bottom:1px solid var(--color-border)">
          <div class="skeleton" style="width:32px;height:32px;border-radius:8px;flex-shrink:0" />
          <div style="flex:1">
            <div class="skeleton" style="height:13px;width:55%;border-radius:4px;margin-bottom:6px" />
            <div class="skeleton" style="height:11px;width:35%;border-radius:4px" />
          </div>
          <div class="skeleton" style="height:11px;width:60px;border-radius:4px" />
          <div class="skeleton" style="height:11px;width:80px;border-radius:4px" />
        </div>
      ))}
    </div>
  );
}

// ─── Folder sidebar (hiërarchisch: sectie → commissie/handmatige map → …) ────

function FolderSidebar({ folders, selectedFolder, sharedWithMe, onSelect, onSharedWithMe, onNewFolder, onEditFolder, canManage }) {
  const totalCount = folders.reduce((s, f) => s + (f.doc_count || 0), 0);

  // Top level: club-wide folders (no section), section folders, and folders whose
  // parent this user cannot see (e.g. a folder opened to one of their categories).
  const topLevel        = topLevelFolders(folders);
  const clubWideFolders = topLevel.filter(f => !f.section_id && !isAutoFolder(f));
  const sectionFolders  = topLevel.filter(f => isSectionRoot(f));
  // A top-level folder for a section (not its own folder) shows under that section's header
  const underSection = (f) => !f.parent_id && f.section_id && !isAutoFolder(f) && !isSectionRoot(f)
    && sectionFolders.find(sf => sf.section_id === f.section_id);
  const otherFolders    = topLevel.filter(f => !clubWideFolders.includes(f) && !sectionFolders.includes(f) && !underSection(f));
  const childrenOf = (id) => folders.filter(f => f.parent_id === id || underSection(f)?.id === id);

  // Track which folder nodes are expanded
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  // Auto-expand every ancestor of the selected folder
  useEffect(() => {
    if (!selectedFolder) return;
    const sel = folders.find(f => f.id === selectedFolder);
    if (!sel) return;
    const ancestors = folderPath(sel, folders).slice(0, -1);
    if (ancestors.length) {
      setExpanded(prev => ({ ...prev, ...Object.fromEntries(ancestors.map(a => [a.id, true])) }));
    }
  }, [selectedFolder, folders]);

  const isActive = (id) => selectedFolder === id && !sharedWithMe;

  // One folder plus (when expanded) its subfolders, at any depth.
  // depth 0 = directly under a section header / group header.
  function FolderNode({ folder, depth = 0 }) {
    const children = childrenOf(folder.id);
    const open = !!expanded[folder.id];
    const active = isActive(folder.id);
    const bg = active ? 'var(--color-primary)' : 'transparent';
    const color = active ? '#fff' : 'var(--color-text)';
    const restricted = folder.access_category_ids?.length > 0;
    return (
      <div>
        <div style="display:flex;align-items:center">
          <button
            onClick={() => { if (children.length) toggle(folder.id); onSelect(folder.id); }}
            style={`flex:1;display:flex;align-items:center;gap:8px;padding:6px 10px 6px ${16 + depth * 14}px;border:none;background:${bg};color:${color};cursor:pointer;text-align:left;font-size:${depth === 0 ? 12 : 13}px;font-weight:${depth === 0 ? 600 : 500};transition:background .15s;min-width:0`}
          >
            <span style="font-size:13px;flex-shrink:0">{folder.icon}</span>
            <span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{folder.name}</span>
            {restricted && <span title="Beperkt tot gekozen categorieën" style="flex-shrink:0;opacity:.7"><LockIcon /></span>}
            {(folder.doc_count > 0) && (
              <span style={`font-size:10px;padding:1px 5px;border-radius:10px;background:${active ? 'rgba(255,255,255,.25)' : 'var(--color-border)'};color:${active ? '#fff' : 'var(--color-text-muted)'};flex-shrink:0`}>
                {folder.doc_count}
              </span>
            )}
            {children.length > 0 && (
              <span style={`font-size:10px;transition:transform .15s;transform:rotate(${open?'90deg':'0deg'});opacity:.6;flex-shrink:0`}>▶</span>
            )}
          </button>
          {canManage && folder.can_manage !== false && (
            <button onClick={(e) => { e.stopPropagation(); onEditFolder(folder); }} title="Map bewerken"
              style={`padding:5px 7px;border:none;background:transparent;color:var(--color-text-muted);cursor:pointer;font-size:11px;opacity:${active?'1':'.3'};transition:opacity .15s`}>
              ⚙️
            </button>
          )}
        </div>
        {open && children.map(f => <FolderNode key={f.id} folder={f} depth={depth + 1} />)}
      </div>
    );
  }

  function SectionGroup({ sectionFolder }) {
    const children = childrenOf(sectionFolder.id);
    const open = !!expanded[sectionFolder.id];
    return (
      <div>
        <div style={`padding:6px 16px 2px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:${isActive(sectionFolder.id) ? 'var(--color-primary)' : 'var(--color-text-muted)'};border-top:1px solid var(--color-border);margin-top:2px;display:flex;align-items:center;gap:6px;cursor:pointer`}
          onClick={() => { toggle(sectionFolder.id); onSelect(sectionFolder.id); }}>
          <span style="flex:1;min-width:0">{folderLabel(sectionFolder)}</span>
          <DepartmentLabels targets={[{ type: 'section', id: sectionFolder.section_id }]} />
          {children.length > 0 && <span style={`font-size:9px;transition:transform .15s;transform:rotate(${open?'90deg':'0deg'});opacity:.5`}>▶</span>}
          {canManage && sectionFolder.can_manage !== false && (
            <button
              onClick={(e) => { e.stopPropagation(); onEditFolder(sectionFolder); }}
              title="Map bewerken"
              style="padding:2px 5px;border:none;background:transparent;color:var(--color-text-muted);cursor:pointer;font-size:11px;opacity:.4;transition:opacity .15s;line-height:1"
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '.4'}
            >⚙️</button>
          )}
        </div>
        {open && children.map(f => <FolderNode key={f.id} folder={f} depth={1} />)}
      </div>
    );
  }

  function SectionHeader({ name }) {
    return (
      <div style="padding:6px 16px 2px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--color-text-muted);border-top:1px solid var(--color-border);margin-top:2px">
        {name}
      </div>
    );
  }

  return (
    <div style="width:230px;flex-shrink:0;border-right:1px solid var(--color-border);background:var(--color-surface);border-radius:12px 0 0 12px;overflow:hidden;display:flex;flex-direction:column">
      <div style="padding:14px 16px 6px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--color-text-muted)">
        Mappen
      </div>

      <button onClick={() => onSelect(null)}
        style={`display:flex;align-items:center;gap:10px;padding:7px 16px;border:none;background:${selectedFolder === null && !sharedWithMe ? 'var(--color-primary)' : 'transparent'};color:${selectedFolder === null && !sharedWithMe ? '#fff' : 'var(--color-text)'};cursor:pointer;text-align:left;width:100%;font-size:13px;font-weight:500;transition:background .15s`}
      >
        <span style="font-size:15px">📂</span>
        <span style="flex:1">Alle documenten</span>
        <span style={`font-size:11px;padding:1px 6px;border-radius:10px;background:${selectedFolder === null && !sharedWithMe ? 'rgba(255,255,255,.25)' : 'var(--color-border)'};color:${selectedFolder === null && !sharedWithMe ? '#fff' : 'var(--color-text-muted)'}`}>
          {totalCount}
        </span>
      </button>

      <button onClick={onSharedWithMe}
        style={`display:flex;align-items:center;gap:10px;padding:7px 16px;border:none;background:${sharedWithMe ? 'var(--color-primary)' : 'transparent'};color:${sharedWithMe ? '#fff' : 'var(--color-text)'};cursor:pointer;text-align:left;width:100%;font-size:13px;font-weight:500;transition:background .15s`}
      >
        <span style="font-size:15px">👥</span>
        <span style="flex:1">Gedeeld met mij</span>
      </button>

      <div style="flex:1;overflow-y:auto;padding-bottom:8px">
        {clubWideFolders.length > 0 && (
          <>
            <SectionHeader name="Iedereen" />
            {clubWideFolders.map(f => <FolderNode key={f.id} folder={f} depth={0} />)}
          </>
        )}
        {sectionFolders.map(sf => <SectionGroup key={sf.id} sectionFolder={sf} />)}
        {otherFolders.length > 0 && (
          <>
            <SectionHeader name="Overig" />
            {otherFolders.map(f => <FolderNode key={f.id} folder={f} depth={0} />)}
          </>
        )}
      </div>

      {canManage && (
        <div style="padding:8px 12px;border-top:1px solid var(--color-border)">
          <button onClick={onNewFolder}
            style="display:flex;align-items:center;gap:8px;padding:7px 10px;border:1px dashed var(--color-border);border-radius:8px;background:transparent;color:var(--color-text-muted);cursor:pointer;font-size:12px;font-weight:500;width:100%;transition:all .15s">
            <span>+</span> Nieuwe map
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Document row ─────────────────────────────────────────────────────────────

// In a sportcomplex: with whom of the linked clubs a document is shared
// Short labels: the row has little room; the title says it in full
const COMPLEX_SHARE = [['', 'Alleen complex'], ['besturen', 'Besturen'], ['leden', 'Alle leden']];
// In the upload and link forms of a sportcomplex: with whom it is shared from the start
function ComplexSharePicker({ value, onChange }) {
  return (
    <div style="margin-bottom:14px">
      <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Gedeeld met</label>
      <select class="form-input" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">Alleen het complex</option>
        <option value="besturen">De besturen van de clubs</option>
        <option value="leden">Alle leden van de clubs</option>
      </select>
    </div>
  );
}

function ComplexShareSelect({ doc, onRefresh }) {
  const [value, setValue] = useState(doc.complex_audience || '');
  const [error, setError] = useState(false);
  async function change(next) {
    const before = value;
    setValue(next); setError(false);
    try { await api(`/api/documents/${doc.id}`, { method: 'PUT', body: JSON.stringify({ complex_audience: next || null }) }); onRefresh?.(); }
    catch { setValue(before); setError(true); }
  }
  return (
    <select class="form-input" value={value} onChange={e => change(e.target.value)} aria-label="Gedeeld met"
      title={error ? 'Opslaan mislukt' : 'Gedeeld met: alleen het complex, de besturen van de clubs of alle leden van de clubs'}
      style={`width:auto;height:28px;padding:0 6px;font-size:11px${error ? ';border-color:var(--color-danger,#dc2626)' : ''}`}>
      {COMPLEX_SHARE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function DocumentRow({ doc, currentUserId, canCreate, canDelete, canManageAccess, folders, onPreview, onDelete, onVersions, onAccess, onMove, onShare, onRefresh, complex }) {
  const isUploader = Number(doc.uploaded_by) === currentUserId;
  // The server says per document what this member may do (a role for a department: only
  // with its documents); the role rights are the fallback
  const mayEdit = doc.can_edit ?? (canManageAccess || isUploader);
  const mayDelete = doc.can_delete ?? (canDelete || isUploader);

  return (
    <div style="display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid var(--color-border);transition:background .15s" class="doc-row">
      {/* File icon */}
      <div style="width:36px;height:36px;border-radius:8px;background:var(--color-bg);display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0;border:1px solid var(--color-border)">
        {fileIcon(doc.file_type)}
      </div>

      {/* Name + description */}
      <div style="flex:2;min-width:0">
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap">
          <span style="font-size:13px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:280px" title={doc.file_name}>
            {doc.file_name}
          </span>
          {(doc.version || 1) > 1 && (
            <span style="font-size:10px;background:var(--color-primary);color:#fff;padding:1px 5px;border-radius:10px;flex-shrink:0">
              v{doc.version}
            </span>
          )}
          {doc.restricted_role_count > 0 && (
            <span style="font-size:10px;color:var(--color-warning,#d97706);display:flex;align-items:center;flex-shrink:0">
              <LockIcon />Beperkt
            </span>
          )}
        </div>
        {doc.description && (
          <div style="font-size:11px;color:var(--color-text-muted);margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title={doc.description}>
            {doc.description}
          </div>
        )}
        <div style="font-size:11px;color:var(--color-text-muted);margin-top:1px">
          {doc.is_external
            ? `${externalServiceLabel(doc.external_url)} · ${doc.uploaded_by_name || '?'} · ${timeAgo(doc.created_at)}`
            : `${formatFileSize(doc.file_size)} · ${doc.uploaded_by_name || '?'} · ${timeAgo(doc.created_at)}`
          }
        </div>
      </div>

      {/* Category badge */}
      {doc.category && (
        <span style="font-size:11px;background:var(--color-border);color:var(--color-text-muted);padding:2px 8px;border-radius:10px;flex-shrink:0;white-space:nowrap;max-width:100px;overflow:hidden;text-overflow:ellipsis" title={doc.category}>
          {doc.category}
        </span>
      )}

      {/* Actions */}
      <div style="display:flex;gap:4px;flex-shrink:0;flex-wrap:wrap;justify-content:flex-end">
        {doc.is_external ? (isHttpUrl(doc.external_url) ? (
          <a
            href={doc.external_url}
            target="_blank"
            rel="noopener noreferrer"
            style="padding:5px 9px;font-size:11px;border:1px solid var(--color-primary);border-radius:6px;background:transparent;color:var(--color-primary);cursor:pointer;white-space:nowrap;text-decoration:none;display:inline-flex;align-items:center;gap:4px"
          >
            ↗ Openen
          </a>
        ) : (
          <span
            title="Ongeldige link: alleen http:// en https:// worden geopend"
            style="padding:5px 9px;font-size:11px;color:var(--color-text-muted);max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"
          >
            {doc.external_url}
          </span>
        )) : (
          <>
            {isPreviewable(doc.file_type) && (
              <button
                onClick={() => onPreview(doc)}
                title="Bekijk"
                style="padding:5px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer;white-space:nowrap"
              >
                👁 Bekijk
              </button>
            )}
            <button
              onClick={() => openFile(`/api/documents/${doc.id}/download`, doc.file_name)}
              title="Download"
              style="padding:5px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer"
            >
              ⬇
            </button>
            <button
              onClick={() => onVersions(doc)}
              title="Versies"
              style="padding:5px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer"
            >
              🕐
            </button>
          </>
        )}
        {mayEdit && !doc.is_external && (
          <button
            onClick={() => onAccess(doc)}
            title="Roltoegang"
            style="padding:5px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer"
          >
            🔐
          </button>
        )}
        {mayEdit && complex && <ComplexShareSelect doc={doc} onRefresh={onRefresh} />}
        {mayEdit && !complex && (
          <button
            onClick={() => onShare(doc)}
            title="Delen met andere sectie"
            style="padding:5px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer"
          >
            👥
          </button>
        )}
        {mayEdit && folders.length > 0 && (
          <button
            onClick={() => onMove(doc)}
            title="Verplaatsen"
            style="padding:5px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer"
          >
            📁
          </button>
        )}
        {mayDelete && (
          <button
            onClick={() => onDelete(doc)}
            title="Verwijder"
            style="padding:5px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:transparent;color:var(--color-danger,#dc2626);border-color:var(--color-danger,#dc2626);cursor:pointer"
          >
            🗑
          </button>
        )}
      </div>
    </div>
  );
}

// ─── External link modal ──────────────────────────────────────────────────────

function ExternalLinkModal({ folders, defaultFolder, onClose, onAdded, complex }) {
  const [url, setUrl]           = useState('');
  const [sharedWith, setSharedWith] = useState('');
  const [name, setName]         = useState('');
  const [folderId, setFolderId] = useState(defaultFolder ? String(defaultFolder) : '');
  const [description, setDesc]  = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  async function handleSave() {
    setError('');
    if (!url.trim()) return setError('URL is verplicht.');
    if (!name.trim()) return setError('Naam is verplicht.');
    if (!isHttpUrl(url.trim())) return setError('Voer een geldige URL in (inclusief https://).');

    setSaving(true);
    try {
      await api('/api/documents/link', {
        method: 'POST',
        body: JSON.stringify({ url: url.trim(), name: name.trim(), folderId: folderId ? Number(folderId) : null, description: description || null, ...(complex ? { complex_audience: sharedWith || null } : {}) }),
      });
      onAdded();
      onClose();
    } catch (err) {
      setError(err.detail || 'Er ging iets mis. Probeer het opnieuw.');
      setSaving(false);
    }
  }

  return (
    <Modal isOpen title="Externe link toevoegen" onClose={onClose}>
      {error && <div class="alert alert-error mb-3">{error}</div>}

      <div style="margin-bottom:14px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">URL *</label>
        <input
          class="form-input"
          type="url"
          value={url}
          onInput={e => {
            setUrl(e.target.value);
            if (!name) {
              try {
                const host = new URL(e.target.value).hostname.replace('www.', '');
                setName(host);
              } catch {}
            }
          }}
          placeholder="https://mijnclub.sharepoint.com/..."
          autoFocus
        />
        <div style="font-size:11px;color:var(--color-text-muted);margin-top:4px">
          Werkt met SharePoint, OneDrive, Google Drive, Dropbox, Notion en meer
        </div>
      </div>

      <div style="margin-bottom:14px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Naam *</label>
        <input
          class="form-input"
          value={name}
          onInput={e => setName(e.target.value)}
          placeholder="bv. Ledenlijst 2025, Notulen ALV..."
        />
      </div>

      <div style="margin-bottom:14px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Omschrijving</label>
        <input
          class="form-input"
          value={description}
          onInput={e => setDesc(e.target.value)}
          placeholder="Korte omschrijving (optioneel)"
        />
      </div>

      <div style="margin-bottom:20px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Map</label>
        <select class="form-input" value={folderId} onChange={e => setFolderId(e.target.value)}>
          <option value="">— Geen map —</option>
          {folderOptions(folders).map(o => <option key={o.id} value={String(o.id)}>{o.label}</option>)}
        </select>
      </div>

      {complex && <ComplexSharePicker value={sharedWith} onChange={setSharedWith} />}

      <div class="flex gap-2 justify-end">
        <Button variant="secondary" onClick={onClose} disabled={saving}>Annuleer</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Opslaan...' : 'Link toevoegen'}
        </Button>
      </div>
    </Modal>
  );
}

// ─── Folder modal (create / edit) ─────────────────────────────────────────────

// Checklist of club categories, grouped section → commission, with a filter box.
function CategoryPicker({ sectionTree, selected, onChange }) {
  const [filter, setFilter] = useState('');
  const q = filter.trim().toLowerCase();
  const match = (cat, ...parents) => !q || [cat.name, ...parents].some(n => n?.toLowerCase().includes(q));
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);

  const CatRow = ({ cat, indent }) => (
    <label style={`display:flex;align-items:center;gap:8px;padding:5px 10px 5px ${10 + indent * 14}px;cursor:pointer;font-size:13px`}>
      <input type="checkbox" checked={selected.includes(cat.id)} onChange={() => toggle(cat.id)} />
      {cat.name}
    </label>
  );
  const groupStyle = 'padding:6px 10px 2px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--color-text-muted)';

  const groups = (sectionTree || []).map(s => {
    const direct = (s.categories || []).filter(c => match(c, s.name));
    const comms = (s.commissions || [])
      .map(c => ({ ...c, cats: (c.categories || []).filter(cat => match(cat, c.name, s.name)) }))
      .filter(c => c.cats.length);
    return { s, direct, comms };
  }).filter(g => g.direct.length || g.comms.length);

  const names = clubStructureNames(sectionTree).categories;

  return (
    <div>
      {selected.length > 0 && (
        <div style="display:flex;flex-wrap:wrap;gap:4px;margin-bottom:6px">
          {selected.map(id => (
            <button key={id} type="button" onClick={() => toggle(id)} title="Verwijderen"
              style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border:1px solid var(--color-primary);border-radius:10px;background:transparent;color:var(--color-primary);cursor:pointer;font-size:11px">
              {names[id] || `#${id}`} ×
            </button>
          ))}
        </div>
      )}
      <input class="form-input" value={filter} onInput={e => setFilter(e.target.value)}
        placeholder="Zoek functie, commissie of sectie..." style="margin-bottom:6px;height:32px;font-size:13px" />
      <div style="border:1px solid var(--color-border);border-radius:8px;max-height:220px;overflow-y:auto">
        {groups.length === 0 && (
          <div style="padding:10px;font-size:12px;color:var(--color-text-muted)">Niets gevonden</div>
        )}
        {groups.map(({ s, direct, comms }) => (
          <div key={s.id} style="border-bottom:1px solid var(--color-border)">
            <div style={groupStyle}>{s.name}</div>
            {direct.map(cat => <CatRow key={cat.id} cat={cat} indent={0} />)}
            {comms.map(c => (
              <div key={c.id}>
                <div style="padding:4px 10px 2px 24px;font-size:12px;font-weight:600">{c.name}</div>
                {c.cats.map(cat => <CatRow key={cat.id} cat={cat} indent={2} />)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function FolderModal({ folder, sections, onClose, onSave, onDelete }) {
  const isEdit = !!folder?.id;
  const isSubfolder = !!folder?.parent_id;
  const isAuto = isAutoFolder(folder);
  const [name, setName]           = useState(folder?.name || '');
  const [color, setColor]         = useState(folder?.color || '#6366f1');
  const [icon, setIcon]           = useState(folder?.icon || '📁');
  const [sectionId, setSectionId] = useState(folder?.section_id ? String(folder.section_id) : '');
  // Auto folders start from the categories the club structure gives them.
  const names = clubStructureNames(sections);
  const automaticIds = !isAuto ? []
    : folder.category_id ? [folder.category_id]
    : sections.flatMap(s => s.commissions || []).find(c => c.id === folder.commission_id)?.categories.map(c => c.id) || [];
  const hasOverride = folder?.access_category_ids?.length > 0;
  // Auto folders are either "automatic" (follow the structure, saved as an empty list so
  // categories added later get access too) or "custom" (the ticked list, even when it
  // matches the structure — that still excludes supervisors and future categories).
  const [automatic, setAutomatic] = useState(isAuto && !hasOverride);
  const [customIds, setCustomIds] = useState(folder?.access_category_ids || []);
  const isAutomatic = isAuto && automatic;
  // Why derived: the sections tree may arrive after the modal opened.
  const categoryIds = isAutomatic ? automaticIds : customIds;
  const setCategoryIds = (ids) => {
    // Unticking everything on an auto folder puts it back on the structure.
    if (isAuto && ids.length === 0) { setAutomatic(true); return; }
    setCustomIds(ids);
    setAutomatic(false);
  };
  const autoSource = folder?.category_id
    ? `de categorie ${names.categories[folder.category_id] || ''}`
    : `de commissie ${names.commissions[folder?.commission_id] || ''}`;
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [error, setError]         = useState(null);

  const ICONS = ['📁','📂','📋','📌','📎','🗂','📊','📈','📝','🔒','🌟','🎯','🏆','⚙️','👥','📅'];

  const handleSave = async () => {
    if (!name.trim()) { setError('Naam is verplicht'); return; }
    setSaving(true);
    try {
      await onSave({
        id: folder?.id,
        name: name.trim(),
        color,
        icon,
        section_id: sectionId ? parseInt(sectionId) : null,
        parent_id: folder?.parent_id || null,
        category_ids: isAutomatic ? [] : categoryIds,
      });
      onClose();
    } catch (e) { setError(e.message || 'Fout'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!confirm(`Map "${folder.name}" verwijderen? Documenten worden niet verwijderd.`)) return;
    setDeleting(true);
    try { await onDelete(folder.id); onClose(); }
    catch (e) { setError(translateError(e.code, e.detail) || 'Fout'); }
    finally { setDeleting(false); }
  };

  return (
    <Modal isOpen title={isEdit ? 'Map bewerken' : isSubfolder ? 'Nieuwe submap' : 'Nieuwe map'} onClose={onClose}>
      {error && <div class="alert alert-error mb-4">{error}</div>}
      {isAuto && (
        <div style="margin-bottom:12px;padding:8px 12px;background:var(--color-border);border-radius:7px;font-size:12px;color:var(--color-text-muted)">
          Automatisch aangemaakt vanuit de clubstructuur ({autoSource.replace(/^de /, '')}).
        </div>
      )}
      <div style="margin-bottom:16px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:6px">Naam *</label>
        <input
          class="form-input"
          value={name}
          onInput={e => setName(e.target.value)}
          placeholder="bv. Notulen, Statuten..."
          maxLength={60}
        />
      </div>
      {!isSubfolder && !isAuto && (
      <div style="margin-bottom:16px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:6px">Zichtbaar voor</label>
        <select class="form-input" value={sectionId} onChange={e => setSectionId(e.target.value)}>
          <option value="">Hele club (iedereen)</option>
          {sections.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
        </select>
        <div style="font-size:11px;color:var(--color-text-muted);margin-top:4px">
          Alleen leden van deze sectie zien de map en zijn documenten.
        </div>
      </div>
      )}
      <div style="margin-bottom:16px">
        <label style="display:flex;align-items:center;gap:6px;font-size:13px;font-weight:600;margin-bottom:6px">
          <span>
            Toegang voor categorieën
            <span style="font-weight:400;color:var(--color-text-muted)">
              {isAuto
                ? (isAutomatic ? ' · automatisch' : ` · aangepast · ${categoryIds.length} gekozen`)
                : (categoryIds.length > 0 ? ` · ${categoryIds.length} gekozen` : '')}
            </span>
          </span>
          {isAuto && !isAutomatic && (
            <button type="button" onClick={() => setAutomatic(true)}
              style="margin-left:auto;padding:2px 8px;border:1px solid var(--color-border);border-radius:6px;background:transparent;color:var(--color-text-muted);cursor:pointer;font-size:11px;font-weight:500">
              Terug naar automatisch
            </button>
          )}
        </label>
        <CategoryPicker sectionTree={sections} selected={categoryIds} onChange={setCategoryIds} />
        <div style="font-size:11px;color:var(--color-text-muted);margin-top:4px">
          {isAutomatic
            ? `Volgt ${autoSource}. Nieuwe categorieën daarin krijgen automatisch toegang.`
            : categoryIds.length > 0
              ? `Alleen leden met een van deze categorieën zien de map, de submappen en de documenten. Functioneel beheerders en documentbeheerders zien hem altijd.${isAuto ? ' Toezichthoudende categorieën alleen als je ze aanvinkt.' : ''}`
              : isSubfolder
                ? 'Niets gekozen: de map heeft dezelfde toegang als de bovenliggende map.'
                : 'Niets gekozen: de toegang volgt "Zichtbaar voor".'}
        </div>
      </div>
      <div style="margin-bottom:16px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:6px">Pictogram</label>
        <div style="display:flex;flex-wrap:wrap;gap:8px">
          {ICONS.map(ic => (
            <button
              key={ic}
              onClick={() => setIcon(ic)}
              style={`font-size:20px;padding:6px 8px;border-radius:8px;border:2px solid ${icon === ic ? 'var(--color-primary)' : 'var(--color-border)'};background:${icon === ic ? 'var(--color-primary)10' : 'transparent'};cursor:pointer;transition:all .15s`}
            >
              {ic}
            </button>
          ))}
        </div>
      </div>
      <div style="margin-bottom:24px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:6px">Kleur</label>
        <div style="display:flex;align-items:center;gap:10px">
          <input type="color" value={color} onInput={e => setColor(e.target.value)} style="width:40px;height:32px;border:1px solid var(--color-border);border-radius:6px;cursor:pointer;padding:2px" />
          <span style="font-size:13px;color:var(--color-text-muted)">{color}</span>
        </div>
      </div>
      <div class="flex gap-2 justify-end" style={isEdit ? 'justify-content:space-between' : ''}>
        {isEdit && (
          <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting || saving}>
            {deleting ? '...' : 'Verwijder map'}
          </Button>
        )}
        <div class="flex gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Annuleer</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>{saving ? '...' : 'Opslaan'}</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Upload modal ─────────────────────────────────────────────────────────────

function UploadModal({ folders, defaultFolder, structureNames, onClose, onUploaded, canManageAccess, complex }) {
  const fileRef = useRef(null);
  const [sharedWith, setSharedWith] = useState('');
  const [file, setFile]             = useState(null);
  const [dragging, setDragging]     = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [folderId, setFolderId]     = useState(defaultFolder ? String(defaultFolder) : '');
  const selectedFolderObj           = folders.find(f => String(f.id) === folderId);
  const [roleIds, setRoleIds]       = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [uploading, setUploading]   = useState(false);
  const [error, setError]           = useState(null);

  useEffect(() => {
    if (canManageAccess) {
      api('/api/users/roles/list').then(setAvailableRoles).catch(() => {});
    }
  }, [canManageAccess]);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    if (!displayName) setDisplayName(f.name);
  };

  const handleFileChange = (e) => pickFile(e.target.files[0]);

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) pickFile(f);
  };

  const toggleRole = (id) => setRoleIds(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);

  const handleUpload = async () => {
    if (!file) { setError('Selecteer een bestand'); return; }
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (displayName.trim()) form.append('displayName', displayName.trim());
      if (description.trim()) form.append('description', description.trim());
      if (folderId) form.append('folderId', folderId);
      if (roleIds.length) form.append('roleIds', JSON.stringify(roleIds));
      if (complex && sharedWith) form.append('complex_audience', sharedWith);

      const res = await fetch('/api/documents', { method: 'POST', body: form });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { const e = new Error(data.error || 'server_error'); e.code = data.error; throw e; }
      onUploaded(data);
      onClose();
    } catch (e) {
      setError(translateError(e.code, e.detail) || 'Upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const dropZoneActive = dragging || !!file;

  return (
    <Modal isOpen title="Document uploaden" onClose={onClose}>
      {error && <div class="alert alert-error mb-4">{error}</div>}

      {/* File picker / drop zone */}
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={`margin-bottom:16px;border:2px dashed ${dragging ? 'var(--color-primary)' : file ? 'var(--color-primary)' : 'var(--color-border)'};border-radius:10px;padding:24px;text-align:center;cursor:pointer;transition:all .15s;background:${dragging ? 'var(--color-primary)18' : file ? 'var(--color-primary)08' : 'transparent'}`}
      >
        <div style="font-size:28px;margin-bottom:8px">{dragging ? '🎯' : file ? fileIcon(file.type) : '📤'}</div>
        <div style="font-size:13px;font-weight:600">
          {dragging ? 'Laat los om te uploaden' : file ? file.name : 'Sleep een bestand hierheen'}
        </div>
        <div style="font-size:11px;color:var(--color-text-muted);margin-top:4px">
          {file ? formatFileSize(file.size) : 'of klik om te bladeren · max 10 MB'}
        </div>
        <input ref={fileRef} type="file" style="display:none" onChange={handleFileChange} />
      </div>

      {/* Display name */}
      <div style="margin-bottom:12px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Weergavenaam</label>
        <input class="form-input" value={displayName} onInput={e => setDisplayName(e.target.value)} placeholder="Laat leeg om bestandsnaam te gebruiken" />
      </div>

      {/* Description */}
      <div style="margin-bottom:12px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Omschrijving</label>
        <textarea
          class="form-input"
          value={description}
          onInput={e => setDescription(e.target.value)}
          placeholder="Korte omschrijving (optioneel)"
          rows={2}
          style="resize:vertical;min-height:56px"
        />
        <AiAssist kind="document_description" value={description} context={{ title: displayName || file?.name || '' }} onUse={setDescription} />
      </div>

      {/* Folder */}
      <div style="margin-bottom:12px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Map</label>
        <select class="form-input" value={folderId} onChange={e => setFolderId(e.target.value)}>
          <option value="">— Geen map —</option>
          {folderOptions(folders).map(o => <option key={o.id} value={String(o.id)}>{o.label}</option>)}
        </select>
        {selectedFolderObj && (
          <div style="font-size:11px;color:var(--color-text-muted);margin-top:4px;display:flex;align-items:center;gap:2px">
            {selectedFolderObj.audience?.type !== 'all' && <LockIcon />}
            Zichtbaar voor: {audienceLabel(selectedFolderObj.audience, structureNames)}
          </div>
        )}
      </div>

      {/* Role access (only for admins) */}
      {canManageAccess && availableRoles.length > 0 && (
        <div style="margin-bottom:16px">
          <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">
            Toegang beperken <span style="font-size:11px;color:var(--color-text-muted);font-weight:400">(leeg = iedereen)</span>
          </label>
          <div style="border:1px solid var(--color-border);border-radius:8px;overflow:hidden;max-height:140px;overflow-y:auto">
            {availableRoles.map(role => (
              <label key={role.id} style="display:flex;align-items:center;gap:10px;padding:8px 12px;border-bottom:1px solid var(--color-border);cursor:pointer;font-size:13px">
                <input type="checkbox" checked={roleIds.includes(role.id)} onChange={() => toggleRole(role.id)} />
                {role.name}
              </label>
            ))}
          </div>
        </div>
      )}

      {complex && <ComplexSharePicker value={sharedWith} onChange={setSharedWith} />}

      <div class="flex gap-2 justify-end">
        <Button variant="secondary" onClick={onClose} disabled={uploading}>Annuleer</Button>
        <Button variant="primary" onClick={handleUpload} disabled={uploading || !file}>
          {uploading ? 'Uploaden...' : 'Uploaden'}
        </Button>
      </div>
    </Modal>
  );
}

// ─── Move modal ───────────────────────────────────────────────────────────────

function MoveModal({ doc, folders, onClose, onMoved }) {
  const currentFolder = doc?.folder_id || folders.find(f => f.name === doc?.category)?.id;
  const [folderId, setFolderId] = useState(currentFolder ? String(currentFolder) : '');
  const [saving, setSaving] = useState(false);
  // Why: when the current folder is not in this user's list the select shows "Geen map";
  // saving that unchanged would silently make the document club-wide.
  const unchanged = folderId === (currentFolder ? String(currentFolder) : '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await api(`/api/documents/${doc.id}`, {
        method: 'PUT',
        body: JSON.stringify({ folder_id: folderId ? Number(folderId) : null }),
      });
      onMoved();
      onClose();
    } catch { setSaving(false); }
  };

  return (
    <Modal isOpen title={`"${doc?.file_name}" verplaatsen`} onClose={onClose}>
      <div style="margin-bottom:20px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:6px">Verplaats naar map</label>
        <select class="form-input" value={folderId} onChange={e => setFolderId(e.target.value)}>
          <option value="">— Geen map —</option>
          {folderOptions(folders).map(o => <option key={o.id} value={String(o.id)}>{o.label}</option>)}
        </select>
      </div>
      <div class="flex gap-2 justify-end">
        <Button variant="secondary" onClick={onClose} disabled={saving}>Annuleer</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || unchanged}>{saving ? '...' : 'Verplaatsen'}</Button>
      </div>
    </Modal>
  );
}

// ─── Share modal ──────────────────────────────────────────────────────────────

const SHARE_TYPE_COLOR = { section: '#6366f1', commission: '#8b5cf6', category: '#a78bfa', user: '#0ea5e9' };
const SHARE_TYPE_GROUP_LABEL = { section: 'Secties', commission: 'Commissies', category: 'Subcommissies', user: 'Personen' };
const SHARE_TYPE_ORDER = ['section', 'commission', 'category', 'user'];

function ShareModal({ doc, onClose, onShared }) {
  const [selected, setSelected]   = useState([]);
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState({ sections: [], commissions: [], categories: [], users: [] });
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!doc) return;
    setLoading(true);
    api(`/api/documents/${doc.id}/shares`)
      .then(shares => { setSelected(shares); setLoadFailed(false); })
      // Why: saving an empty list after a failed load would silently remove every share.
      .catch(() => { setLoadFailed(true); setError('Huidige deling kon niet worden geladen. Probeer het opnieuw.'); })
      .finally(() => setLoading(false));
  }, [doc?.id]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const data = await api(`/api/documents/share-targets?q=${encodeURIComponent(query)}`);
        setResults(data);
      } catch { setResults({ sections: [], commissions: [], categories: [], users: [] }); }
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  const isSelected = (type, id) => selected.some(s => s.type === type && s.id === id);

  const toggle = (item) => {
    setSelected(prev => isSelected(item.type, item.id)
      ? prev.filter(s => !(s.type === item.type && s.id === item.id))
      : [...prev, item]);
  };

  const remove = (type, id) => setSelected(prev => prev.filter(s => !(s.type === type && s.id === id)));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api(`/api/documents/${doc.id}/shares`, {
        method: 'PUT',
        body: JSON.stringify({ targets: selected.map(s => ({ type: s.type, id: s.id })) }),
      });
      onShared();
      onClose();
    } catch { setError('Opslaan mislukt.'); }
    finally { setSaving(false); }
  };

  const grouped = {
    section:    (results.sections    || []).filter(r => !isSelected('section',    r.id)),
    commission: (results.commissions || []).filter(r => !isSelected('commission', r.id)),
    category:   (results.categories  || []).filter(r => !isSelected('category',  r.id)),
    user:       (results.users       || []).filter(r => !isSelected('user',       r.id)),
  };
  const totalResults = SHARE_TYPE_ORDER.reduce((n, t) => n + grouped[t].length, 0);

  return (
    <Modal isOpen title={`Delen: "${doc?.file_name}"`} onClose={onClose}>
      <p style="font-size:13px;color:var(--color-text-muted);margin-bottom:12px;line-height:1.5">
        Zoek en selecteer secties, commissies of personen hieronder.
      </p>
      {error && <div class="alert alert-error mb-3">{error}</div>}

      {/* Search + results — één geheel met één border */}
      <div style="border:1px solid var(--color-border);border-radius:8px;overflow:hidden;margin-bottom:16px">
        <div style="position:relative;border-bottom:1px solid var(--color-border)">
          <svg style="position:absolute;left:11px;top:50%;transform:translateY(-50%);opacity:0.35;pointer-events:none;flex-shrink:0" width="15" height="15" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
          </svg>
          <input
            type="text"
            placeholder="Zoek sectie, commissie of persoon..."
            value={query}
            onInput={e => setQuery(e.target.value)}
            style="display:block;width:100%;padding:10px 12px 10px 34px;font-size:13px;border:none;outline:none;box-shadow:none;background:transparent;color:var(--color-text);font-family:inherit"
            autoFocus
          />
        </div>

      {/* Inline results list — always visible, fixed height */}
      <div style="height:220px;overflow-y:scroll;background:var(--color-bg)">
        {totalResults === 0 ? (
          <div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:13px;color:var(--color-text-muted)">
            {query ? 'Geen resultaten gevonden' : 'Typ om te zoeken'}
          </div>
        ) : (
          SHARE_TYPE_ORDER.map(type => grouped[type].length > 0 && (
            <div key={type}>
              <div style="padding:6px 12px 4px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--color-text-muted);border-bottom:1px solid var(--color-border);position:sticky;top:0;background:var(--color-bg);z-index:1">
                {SHARE_TYPE_GROUP_LABEL[type]}
              </div>
              {grouped[type].map(item => (
                <button
                  key={`${type}-${item.id}`}
                  onClick={() => toggle({ ...item, type })}
                  style="display:flex;align-items:center;gap:8px;width:100%;padding:9px 12px;border:none;border-bottom:1px solid var(--color-border);background:transparent;cursor:pointer;font-size:13px;text-align:left;color:var(--color-text);transition:background .1s"
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-subtle,rgba(0,0,0,.04))'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={`width:8px;height:8px;border-radius:50%;background:${SHARE_TYPE_COLOR[type]};flex-shrink:0`}/>
                  <span style="flex:1;min-width:0">
                    {item.name}
                    {item.parent_name && (
                      <span style="margin-left:6px;font-size:11px;color:var(--color-text-muted)">· {item.parent_name}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          ))
        )}
      </div>
      </div>

      {/* Selected chips */}
      <div style="margin-bottom:16px">
        <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.06em;color:var(--color-text-muted);margin-bottom:6px">
          Gedeeld met {selected.length > 0 && `(${selected.length})`}
        </div>
        {loading ? (
          <div style="font-size:13px;color:var(--color-text-muted)">Laden...</div>
        ) : selected.length > 0 ? (
          <div style="border:1px solid var(--color-border);border-radius:8px;padding:8px;min-height:48px;max-height:110px;overflow-y:scroll;display:flex;flex-wrap:wrap;gap:6px;align-content:flex-start">
            {selected.map(s => (
              <span key={`${s.type}-${s.id}`} style={`display:inline-flex;align-items:center;gap:5px;padding:4px 8px 4px 10px;border-radius:20px;font-size:12px;font-weight:500;background:${SHARE_TYPE_COLOR[s.type]}18;color:${SHARE_TYPE_COLOR[s.type]};border:1px solid ${SHARE_TYPE_COLOR[s.type]}3a;white-space:nowrap`}>
                <span style={`width:5px;height:5px;border-radius:50%;background:${SHARE_TYPE_COLOR[s.type]};flex-shrink:0`}/>
                {s.name}
                <button
                  onClick={() => remove(s.type, s.id)}
                  style="background:none;border:none;cursor:pointer;padding:0 0 0 2px;line-height:1;color:inherit;opacity:0.6;font-size:16px;display:flex;align-items:center"
                >×</button>
              </span>
            ))}
          </div>
        ) : (
          <div style="border:1px dashed var(--color-border);border-radius:8px;padding:12px;font-size:13px;color:var(--color-text-muted);text-align:center">
            Nog niemand geselecteerd
          </div>
        )}
      </div>

      <div class="flex gap-2 justify-end">
        <Button variant="secondary" onClick={onClose} disabled={saving}>Annuleer</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || loading || loadFailed}>
          {saving ? 'Opslaan...' : 'Opslaan'}
        </Button>
      </div>
    </Modal>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export default function DocumentsView() {
  const { hasPermission, isComplex } = useAuth();
  const currentUserId = Number(authState.value.userId);

  const canCreate = hasPermission('documents', 'create');
  const canDelete = hasPermission('documents', 'delete');
  const canManageAccess = hasPermission('documents', 'update');

  // Folders + sections
  const [folders, setFolders]         = useState([]);
  const [allSections, setAllSections] = useState([]);
  // null = all; ?folder=ID opens a folder directly (e.g. from a welcome page)
  const [selectedFolder, setSelectedFolder] = useState(() => {
    const f = new URLSearchParams(window.location.search).get('folder');
    return f && /^\d+$/.test(f) ? Number(f) : null;
  });
  const [sharedWithMe, setSharedWithMe]     = useState(false);

  // Documents
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState('');

  // Modals
  const [showUpload, setShowUpload]         = useState(false);
  const [showLinkModal, setShowLinkModal]   = useState(false);
  const [deleteDoc, setDeleteDoc]           = useState(null);
  const [deleting, setDeleting]             = useState(false);
  const [previewDoc, setPreviewDoc]         = useState(null);
  const [versionDoc, setVersionDoc]         = useState(null);
  const [versions, setVersions]             = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const [versionUploadError, setVersionUploadError] = useState(null);
  const [accessDoc, setAccessDoc]           = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [savingAccess, setSavingAccess]     = useState(false);
  const [loadingAccess, setLoadingAccess]   = useState(false);
  const [folderModal, setFolderModal]       = useState(null);
  const [moveDoc, setMoveDoc]               = useState(null);
  const [shareDoc, setShareDoc]             = useState(null);

  const versionUploadRef = useRef(null);

  // Fetch sections for folder modal + share modal (all sections the admin knows about)
  useEffect(() => {
    api('/api/settings/sections')
      .then(secs => setAllSections(secs || []))
      .catch(() => {});
  }, []);

  // Fetch folders
  const fetchFolders = () => {
    api('/api/documents/folders').then(list => {
      setFolders(list);
      // A linked folder this user can't see: back to all documents
      setSelectedFolder(cur => (cur && typeof cur === 'number' && !list.some(f => f.id === cur) ? null : cur));
    }).catch(() => {});
  };

  // Fetch documents
  const fetchDocuments = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedFolder) params.set('folderId', selectedFolder);
    if (search) params.set('search', search);
    if (sharedWithMe) params.set('sharedWithMe', '1');
    api(`/api/documents?${params}`).then(docs => {
      setDocuments(docs);
      setLoading(false);
    }).catch(err => {
      setError(translateError(err.code, err.detail));
      setLoading(false);
    });
  };

  useEffect(() => { fetchFolders(); }, []);
  useEffect(() => { fetchDocuments(); }, [selectedFolder, search, sharedWithMe]);

  // Folder CRUD
  const handleSaveFolder = async ({ id, name, color, icon, section_id, parent_id, category_ids }) => {
    if (id) {
      await api(`/api/documents/folders/${id}`, { method: 'PUT', body: JSON.stringify({ name, color, icon, section_id, category_ids }) });
    } else {
      await api('/api/documents/folders', { method: 'POST', body: JSON.stringify({ name, color, icon, section_id, parent_id, category_ids }) });
    }
    fetchFolders();
  };

  const handleDeleteFolder = async (id) => {
    await api(`/api/documents/folders/${id}`, { method: 'DELETE' });
    if (selectedFolder === id) setSelectedFolder(null);
    fetchFolders();
    fetchDocuments();
  };

  // Delete document
  const handleDelete = async () => {
    if (!deleteDoc) return;
    setDeleting(true);
    try {
      await api(`/api/documents/${deleteDoc.id}`, { method: 'DELETE' });
      setDocuments(prev => prev.filter(d => d.id !== deleteDoc.id));
      fetchFolders();
      setDeleteDoc(null);
    } catch (err) {
      setError(translateError(err.code, err.detail));
      setDeleteDoc(null);
    } finally { setDeleting(false); }
  };

  // Version history
  const openVersionHistory = async (doc) => {
    setVersionDoc(doc);
    setVersions([]);
    setLoadingVersions(true);
    setVersionUploadError(null);
    try {
      const data = await api(`/api/documents/${doc.id}/versions`);
      setVersions(data);
    } catch { setVersions([]); }
    finally { setLoadingVersions(false); }
  };

  const handleVersionFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !versionDoc) return;
    setUploadingVersion(true);
    setVersionUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`/api/documents/${versionDoc.id}/versions`, { method: 'POST', body: formData });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { const e = new Error(data.error || 'server_error'); e.code = data.error; throw e; }
      const updated = await api(`/api/documents/${versionDoc.id}/versions`);
      setVersions(updated);
      fetchDocuments();
      fetchFolders();
    } catch (e) { setVersionUploadError(translateError(e.code, e.detail)); }
    finally {
      setUploadingVersion(false);
      if (versionUploadRef.current) versionUploadRef.current.value = '';
    }
  };

  // Access management
  const openAccessModal = async (doc) => {
    setAccessDoc(doc);
    setSelectedRoleIds([]);
    setLoadingAccess(true);
    try {
      const [accessData, rolesData] = await Promise.all([
        api(`/api/documents/${doc.id}/access`),
        availableRoles.length ? Promise.resolve(availableRoles) : api('/api/users/roles/list'),
      ]);
      if (!availableRoles.length) setAvailableRoles(rolesData);
      setSelectedRoleIds(accessData.roleIds || []);
    } catch (err) {
      // Why: saving an empty list after a failed load would lift every restriction.
      setAccessDoc(null);
      setError(translateError(err.code, err.detail));
    }
    finally { setLoadingAccess(false); }
  };

  const handleSaveAccess = async () => {
    if (!accessDoc) return;
    setSavingAccess(true);
    try {
      await api(`/api/documents/${accessDoc.id}/access`, { method: 'PUT', body: JSON.stringify({ roleIds: selectedRoleIds }) });
      fetchDocuments();
      setAccessDoc(null);
    } catch (err) { setError(translateError(err.code, err.detail)); }
    finally { setSavingAccess(false); }
  };

  const handleSelectFolder = (name) => {
    setSelectedFolder(name);
    setSharedWithMe(false);
    setSearch('');
  };

  const handleSharedWithMe = () => {
    setSharedWithMe(true);
    setSelectedFolder(null);
    setSearch('');
  };

  const filteredDocs = documents;

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">📄 Documenten</h1>
        {canCreate && (
          <div style="display:flex;gap:8px">
            <Button variant="secondary" size="sm" onClick={() => setShowLinkModal(true)}>
              🔗 Externe link
            </Button>
            <Button variant="primary" size="sm" onClick={() => setShowUpload(true)}>
              + Document uploaden
            </Button>
          </div>
        )}
      </div>

      {error && <div class="alert alert-error mb-4">{error}</div>}

      {/* A club housed at a sportcomplex: what the complex shared */}
      {!isComplex && <ComplexSharedDocuments />}

      {/* Main layout: sidebar + content */}
      <div style="display:flex;gap:0;border:1px solid var(--color-border);border-radius:12px;overflow:hidden;background:var(--color-surface);height:calc(100vh - 220px);min-height:500px">
        <FolderSidebar
          folders={folders}
          selectedFolder={selectedFolder}
          sharedWithMe={sharedWithMe}
          onSelect={handleSelectFolder}
          onSharedWithMe={handleSharedWithMe}
          onNewFolder={() => setFolderModal({})}
          onEditFolder={(f) => setFolderModal(f)}
          canManage={canManageAccess}
        />

        {/* Right pane */}
        <div style="flex:1;min-width:0;display:flex;flex-direction:column;overflow:hidden">
          {/* Toolbar */}
          <div style="flex-shrink:0;display:flex;align-items:center;gap:10px;padding:12px 16px;border-bottom:1px solid var(--color-border);background:var(--color-bg)">
            {/* Breadcrumb */}
            {selectedFolder && (() => {
              const sel = folders.find(f => f.id === selectedFolder);
              const crumbs = sel ? folderPath(sel, folders) : [];
              return crumbs.length > 0 && (
                <div style="display:flex;align-items:center;gap:4px;flex:1;min-width:0;overflow:hidden">
                  <button onClick={() => handleSelectFolder(null)}
                    style="padding:0;border:none;background:transparent;color:var(--color-text-muted);cursor:pointer;font-size:12px;flex-shrink:0;white-space:nowrap">
                    Alle documenten
                  </button>
                  {crumbs.map((c, i) => (
                    <span key={c.id} style="display:flex;align-items:center;gap:4px;min-width:0">
                      <span style="color:var(--color-text-muted);flex-shrink:0">/</span>
                      {i < crumbs.length - 1
                        ? <button onClick={() => handleSelectFolder(c.id)}
                            style="padding:0;border:none;background:transparent;color:var(--color-text-muted);cursor:pointer;font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:120px">
                            {folderLabel(c)}
                          </button>
                        : <span style="font-size:12px;font-weight:600;color:var(--color-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:150px">{folderLabel(c)}</span>
                      }
                    </span>
                  ))}
                </div>
              );
            })()}
            {!selectedFolder && (
              <div style="position:relative;flex:1;max-width:320px">
                <span style="position:absolute;left:10px;top:50%;transform:translateY(-50%);color:var(--color-text-muted);font-size:14px;pointer-events:none">🔍</span>
                <input
                  class="form-input"
                  value={search}
                  onInput={e => setSearch(e.target.value)}
                  placeholder="Zoek op naam..."
                  style="padding-left:32px;height:34px;font-size:13px"
                />
              </div>
            )}
            {selectedFolder && (
              <div style="position:relative;flex:1;max-width:220px">
                <span style="position:absolute;left:10px;top:50%;transform:translateY(-50%);color:var(--color-text-muted);font-size:14px;pointer-events:none">🔍</span>
                <input
                  class="form-input"
                  value={search}
                  onInput={e => setSearch(e.target.value)}
                  placeholder="Zoek..."
                  style="padding-left:32px;height:34px;font-size:13px"
                />
              </div>
            )}
            <span style="font-size:12px;color:var(--color-text-muted);white-space:nowrap;flex-shrink:0">
              {filteredDocs.length} doc{filteredDocs.length !== 1 ? 's' : ''}
              {sharedWithMe ? ' gedeeld' : ''}
            </span>
            {canManageAccess && selectedFolder && folders.find(f => f.id === selectedFolder)?.can_manage !== false && (
              <button
                onClick={() => setFolderModal({ parent_id: selectedFolder })}
                style="display:flex;align-items:center;gap:5px;padding:5px 10px;border:1px dashed var(--color-border);border-radius:7px;background:transparent;color:var(--color-text-muted);cursor:pointer;font-size:12px;font-weight:500;flex-shrink:0;white-space:nowrap;transition:all .15s"
              >
                📁+ Nieuwe map
              </button>
            )}
          </div>

          {/* Sub-folder cards (direct children of selectedFolder) */}
          {selectedFolder && (() => {
            const subFolders = folders.filter(f => f.parent_id === selectedFolder);
            if (!subFolders.length) return null;
            return (
              <div style="flex-shrink:0;padding:12px 16px;border-bottom:1px solid var(--color-border);background:var(--color-bg)">
                <div style="display:flex;flex-wrap:wrap;gap:8px">
                  {subFolders.map(f => (
                    <div key={f.id} style="display:flex;align-items:center;border:1px solid var(--color-border);border-radius:9px;background:var(--color-surface);overflow:hidden">
                      <button
                        onClick={() => handleSelectFolder(f.id)}
                        style="display:flex;align-items:center;gap:8px;padding:8px 12px;border:none;background:transparent;color:var(--color-text);cursor:pointer;font-size:13px;font-weight:500;transition:all .15s;min-width:0"
                      >
                        <span style="font-size:14px;flex-shrink:0">{f.icon}</span>
                        <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:120px">{f.name}</span>
                        {f.doc_count > 0 && (
                          <span style="font-size:10px;padding:1px 5px;border-radius:8px;background:var(--color-border);color:var(--color-text-muted);flex-shrink:0">{f.doc_count}</span>
                        )}
                      </button>
                      {canManageAccess && f.can_manage !== false && (
                        <button
                          onClick={() => setFolderModal(f)}
                          title="Map bewerken"
                          style="padding:8px 9px;border:none;border-left:1px solid var(--color-border);background:transparent;color:var(--color-text-muted);cursor:pointer;font-size:11px;flex-shrink:0;transition:background .15s"
                        >⚙️</button>
                      )}
                    </div>
                  ))}
                  {canManageAccess && folders.find(x => x.id === selectedFolder)?.can_manage !== false && (
                    <button
                      onClick={() => setFolderModal({ parent_id: selectedFolder })}
                      style="display:flex;align-items:center;gap:6px;padding:8px 12px;border:1px dashed var(--color-border);border-radius:9px;background:transparent;color:var(--color-text-muted);cursor:pointer;font-size:12px;transition:all .15s"
                    >
                      + Nieuwe map
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Document list */}
          <div style="flex:1;overflow-y:auto">
            {loading ? (
              <DocumentsSkeleton />
            ) : filteredDocs.length === 0 ? (
              <div style="padding:48px 32px;text-align:center">
                <div style="font-size:40px;margin-bottom:12px">{sharedWithMe ? '👥' : '📭'}</div>
                <div style="font-size:14px;color:var(--color-text-muted)">
                  {search
                    ? 'Geen documenten gevonden voor deze zoekopdracht'
                    : sharedWithMe
                      ? 'Nog geen documenten met jou gedeeld vanuit een andere sectie'
                      : selectedFolder
                        ? `Geen documenten in map "${folders.find(f => f.id === selectedFolder)?.name || ''}"`
                        : 'Nog geen documenten geüpload'}
                </div>
                {canCreate && !search && !sharedWithMe && (
                  <div style="display:flex;gap:8px;justify-content:center;margin-top:16px">
                    <button
                      onClick={() => setShowLinkModal(true)}
                      style="padding:8px 18px;border:1px solid var(--color-border);border-radius:8px;background:transparent;color:var(--color-text-muted);cursor:pointer;font-size:13px;font-weight:500"
                    >
                      🔗 Externe link
                    </button>
                    <button
                      onClick={() => setShowUpload(true)}
                      style="padding:8px 18px;border:1px solid var(--color-primary);border-radius:8px;background:transparent;color:var(--color-primary);cursor:pointer;font-size:13px;font-weight:500"
                    >
                      + Eerste document uploaden
                    </button>
                  </div>
                )}
              </div>
            ) : (
              filteredDocs.map(doc => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  currentUserId={currentUserId}
                  canCreate={canCreate}
                  canDelete={canDelete}
                  canManageAccess={canManageAccess}
                  folders={folders}
                  onPreview={setPreviewDoc}
                  onDelete={setDeleteDoc}
                  onVersions={openVersionHistory}
                  onAccess={openAccessModal}
                  onMove={setMoveDoc}
                  onShare={setShareDoc}
                  onRefresh={() => { fetchDocuments(); fetchFolders(); }}
                  complex={isComplex}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Modals ── */}

      {showLinkModal && (
        <ExternalLinkModal
          folders={folders.filter(f => f.can_upload !== false)}
          defaultFolder={selectedFolder}
          onClose={() => setShowLinkModal(false)}
          onAdded={() => { fetchDocuments(); fetchFolders(); }}
          complex={isComplex}
        />
      )}

      {showUpload && (
        <UploadModal
          folders={folders.filter(f => f.can_upload !== false)}
          defaultFolder={selectedFolder}
          structureNames={clubStructureNames(allSections)}
          canManageAccess={canManageAccess}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { fetchDocuments(); fetchFolders(); }}
          complex={isComplex}
        />
      )}

      {folderModal !== null && (
        <FolderModal
          folder={folderModal}
          sections={allSections}
          onClose={() => setFolderModal(null)}
          onSave={handleSaveFolder}
          onDelete={handleDeleteFolder}
        />
      )}

      {moveDoc && (
        <MoveModal
          doc={moveDoc}
          folders={folders.filter(f => f.can_manage !== false)}
          onClose={() => setMoveDoc(null)}
          onMoved={() => { fetchDocuments(); fetchFolders(); }}
        />
      )}

      {shareDoc && (
        <ShareModal
          doc={shareDoc}
          onClose={() => setShareDoc(null)}
          onShared={() => { fetchDocuments(); }}
        />
      )}

      {/* Delete confirmation */}
      <Modal isOpen={!!deleteDoc} title="Document verwijderen" onClose={() => !deleting && setDeleteDoc(null)}>
        <p style="margin-bottom:24px;color:var(--color-text-muted);line-height:1.6">
          Weet je zeker dat je <strong>{deleteDoc?.file_name}</strong> wilt verwijderen? Alle versies worden verwijderd en dit kan niet ongedaan gemaakt worden.
        </p>
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={() => setDeleteDoc(null)} disabled={deleting}>Annuleer</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Verwijderen...' : 'Verwijder'}
          </Button>
        </div>
      </Modal>

      {/* Preview modal */}
      <Modal isOpen={!!previewDoc} title={previewDoc?.file_name || 'Voorbeeld'} onClose={() => setPreviewDoc(null)}>
        <div style="max-height:70vh;overflow:auto">
          {previewDoc && previewDoc.file_type?.startsWith('image/') ? (
            <img src={`/api/documents/${previewDoc.id}/preview`} alt={previewDoc.file_name} style="max-width:100%;height:auto;display:block" />
          ) : previewDoc?.file_type === 'application/pdf' ? (
            <PdfFrame src={`/api/documents/${previewDoc.id}/preview`} style="width:100%;height:65vh;border:none" title={previewDoc.file_name} />
          ) : (
            <p class="text-muted text-sm" style="padding:32px;text-align:center">Voorbeeld niet beschikbaar voor dit bestandstype.</p>
          )}
        </div>
        <div class="flex justify-end mt-4">
          <Button variant="secondary" onClick={() => setPreviewDoc(null)}>Sluiten</Button>
        </div>
      </Modal>

      {/* Version history modal */}
      <Modal isOpen={!!versionDoc} title="Versiegeschiedenis" onClose={() => setVersionDoc(null)}>
        {loadingVersions ? (
          <div class="text-muted text-sm" style="padding:24px;text-align:center">Laden...</div>
        ) : (
          <div style="margin-bottom:16px">
            {versions.map((v, i) => (
              <div key={v.id} style="display:flex;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid var(--color-border)">
                <div style="flex:1">
                  <div style="font-size:13px;font-weight:500;display:flex;align-items:center;gap:6px">
                    Versie {v.version || (versions.length - i)}
                    {i === 0 && <span style="font-size:10px;background:var(--color-primary);color:#fff;padding:1px 6px;border-radius:10px">Huidig</span>}
                  </div>
                  <div style="font-size:11px;color:var(--color-text-muted);margin-top:2px">
                    {v.file_name} · {formatFileSize(v.file_size)} · {v.uploaded_by_name || '-'} · {timeAgo(v.created_at)}
                  </div>
                </div>
                <button onClick={() => openFile(`/api/documents/${v.id}/download`, v.file_name)} style="padding:5px 10px;font-size:12px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer">
                  ⬇ Download
                </button>
              </div>
            ))}
          </div>
        )}
        {versionUploadError && <div class="alert alert-error mt-2">{versionUploadError}</div>}
        <div class="flex gap-2 justify-end">
          {canCreate && versionDoc?.can_add_version !== false && (
            <>
              <Button variant="primary" size="sm" onClick={() => versionUploadRef.current?.click()} disabled={uploadingVersion}>
                {uploadingVersion ? 'Uploaden...' : '+ Nieuwe versie'}
              </Button>
              <input ref={versionUploadRef} type="file" style="display:none" onChange={handleVersionFileSelect} />
            </>
          )}
          <Button variant="secondary" onClick={() => setVersionDoc(null)}>Sluiten</Button>
        </div>
      </Modal>

      {/* Access management modal */}
      <Modal isOpen={!!accessDoc} title="Toegangsbeheer" onClose={() => !savingAccess && setAccessDoc(null)}>
        <p style="margin-bottom:16px;color:var(--color-text-muted);font-size:13px;line-height:1.5">
          Kies welke rollen dit document mogen zien. Laat leeg voor toegang voor iedereen.
        </p>
        {loadingAccess ? (
          <div class="text-muted text-sm" style="padding:24px;text-align:center">Laden...</div>
        ) : (
          <div style="margin-bottom:20px;border:1px solid var(--color-border);border-radius:8px;overflow:hidden;max-height:240px;overflow-y:auto">
            {availableRoles.filter(r => !['Functioneel Beheerder', 'Documentbeheerder'].includes(r.name)).map(role => (
              <label key={role.id} style="display:flex;align-items:center;gap:10px;padding:10px 12px;border-bottom:1px solid var(--color-border);cursor:pointer;font-size:13px">
                <input
                  type="checkbox"
                  checked={selectedRoleIds.includes(role.id)}
                  onChange={() => setSelectedRoleIds(prev => prev.includes(role.id) ? prev.filter(r => r !== role.id) : [...prev, role.id])}
                  style="width:15px;height:15px;cursor:pointer"
                />
                {role.name}
              </label>
            ))}
          </div>
        )}
        <p style="font-size:12px;color:var(--color-text-muted);margin-bottom:16px">
          {selectedRoleIds.length === 0 ? '🌐 Zichtbaar voor iedereen in de club' : `🔒 Beperkt tot ${selectedRoleIds.length} rol${selectedRoleIds.length !== 1 ? 'len' : ''}`}
        </p>
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={() => setAccessDoc(null)} disabled={savingAccess}>Annuleer</Button>
          <Button variant="primary" onClick={handleSaveAccess} disabled={savingAccess || loadingAccess}>
            {savingAccess ? 'Opslaan...' : 'Opslaan'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
